import { api } from './axios';

/** @typedef {import('../model/battle').BattlePersonRequest} BattlePersonRequest */
/** @typedef {import('../model/battle').BattlePersonResponse} BattlePersonResponse */
/** @typedef {import('../model/battle').UpdateBattlePersonRequest} UpdateBattlePersonRequest */

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы с участниками баттлов (BattlePerson).
 *
 * Роли:     ORGANIZER | PARTICIPANT | JUDGE
 * Статусы:  PENDING | APPROVED | REJECTED | WITHDRAWN
 */
export const battlePersonApi = {
    /**
     * Получить список участников с пагинацией.
     * NOTE: Параметры battleId, role, status, search — серверные расширения,
     * не документированные в API reference, но используемые интерфейсом.
     * @param {Object} params — page, size, sortBy, direction, battleId?, role?, status?, search?
     * @returns {Promise<{content: BattlePersonResponse[], pageable, totalElements, totalPages}>}
     */
    list: async (params = {}) => {
        try {
            const { data } = await api.get('/battle_person', { params });
            return data;
        } catch (error) {
            console.error('[battlePersonApi.list] Failed to fetch battle persons:', error.message);
            throw error;
        }
    },

    /**
     * Получить участника по ID.
     * @param {string} id — UUID участника
     * @returns {Promise<BattlePersonResponse>}
     */
    get: async (id) => {
        validateId(id, 'Battle person ID');
        try {
            const { data } = await api.get(`/battle_person/${id}`);
            return data;
        } catch (error) {
            console.error(`[battlePersonApi.get] Failed to fetch battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавить участника в баттл.
     * @param {Object} payload — BattlePersonRequest
     * @returns {Promise<BattlePersonResponse>}
     */
    create: async (payload) => {
        if (!payload) throw new Error('Battle person payload is required');
        try {
            const { data } = await api.post('/battle_person', payload);
            return data;
        } catch (error) {
            console.error('[battlePersonApi.create] Failed to create battle person:', error.message);
            throw error;
        }
    },

    /**
     * Полностью обновить данные участника.
     * @param {string} id — UUID участника
     * @param {Object} payload — BattlePersonRequest
     * @returns {Promise<BattlePersonResponse>}
     */
    update: async (id, payload) => {
        validateId(id, 'Battle person ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/battle_person/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlePersonApi.update] Failed to update battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Частично обновить данные участника.
     * @param {string} id — UUID участника
     * @param {Object} payload — UpdateBattlePersonRequest { name?, nickname?, city?, role?, seed? }
     * @returns {Promise<BattlePersonResponse>}
     */
    patch: async (id, payload) => {
        validateId(id, 'Battle person ID');
        if (!payload) throw new Error('Patch payload is required');
        try {
            const { data } = await api.patch(`/battle_person/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[battlePersonApi.patch] Failed to patch battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Одобрить заявку участника.
     * @param {string} id — UUID участника
     * @returns {Promise<BattlePersonResponse>}
     */
    approve: async (id) => {
        validateId(id, 'Battle person ID');
        try {
            const { data } = await api.post(`/battle_person/${id}/approve`);
            return data;
        } catch (error) {
            console.error(`[battlePersonApi.approve] Failed to approve battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Отклонить заявку участника.
     * @param {string} id — UUID участника
     * @returns {Promise<BattlePersonResponse>}
     */
    reject: async (id) => {
        validateId(id, 'Battle person ID');
        try {
            const { data } = await api.post(`/battle_person/${id}/reject`);
            return data;
        } catch (error) {
            console.error(`[battlePersonApi.reject] Failed to reject battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Отозвать заявку участника (установить статус WITHDRAWN).
     * @param {string} id — UUID участника
     * @returns {Promise<BattlePersonResponse>}
     */
    withdraw: async (id) => {
        validateId(id, 'Battle person ID');
        try {
            const { data } = await api.post(`/battle_person/${id}/withdraw`);
            return data;
        } catch (error) {
            console.error(`[battlePersonApi.withdraw] Failed to withdraw battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить участника.
     * @param {string} id — UUID участника
     * @returns {Promise<void>}
     */
    remove: async (id) => {
        validateId(id, 'Battle person ID');
        try {
            await api.delete(`/battle_person/${id}`);
        } catch (error) {
            console.error(`[battlePersonApi.remove] Failed to delete battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Дисквалифицировать участника.
     * NOTE: Endpoint не документирован в reference, добавлен как расширение.
     * Дисквалификация возможна независимо от роли участника и его текущего статуса.
     * @param {string} id — UUID участника
     * @param {string} [reason] — Причина дисквалификации
     * @returns {Promise<BattlePersonResponse>}
     */
    disqualify: async (id, reason = null) => {
        validateId(id, 'Battle person ID');
        try {
            const { data } = await api.post(`/battle_person/${id}/disqualify`, reason ? { reason } : {});
            return data;
        } catch (error) {
            console.error(`[battlePersonApi.disqualify] Failed to disqualify battle person ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Пригласить пользователя в баттл (создать запись с ролью).
     * @param {Object} payload — { battleId, profileId, role: 'PARTICIPANT' | 'JUDGE' }
     * @returns {Promise<BattlePersonResponse>}
     */
    invite: async (payload) => {
        if (!payload) throw new Error('Invite payload is required');
        if (!payload.battleId) throw new Error('battleId is required');
        if (!payload.profileId) throw new Error('profileId is required');
        if (!payload.role) throw new Error('role is required');
        try {
            const { data } = await api.post('/battle_person/invite', payload);
            return data;
        } catch (error) {
            console.error('[battlePersonApi.invite] Failed to invite battle person:', error.message);
            throw error;
        }
    }
};

